export type SharedCartItem = {
  product_id: string;
  title: string;
  quantity: number;
  unit_price: number;
  main_image_url?: string;
  stock?: number;
  is_digital?: boolean;
  product_type?: string;
};

export type SharedCartPayloadV1 = {
  v: 1;
  created_at: string;
  store: {
    id: string;
    name: string;
    slug?: string;
  };
  customer?: {
    id?: string;
    name?: string;
    email?: string;
    phone?: string;
  };
  items: SharedCartItem[];
};

type SharedCartPayloadV2Compact = {
  v: 2;
  s: {
    i: string;
    n: string;
    g?: string;
  };
  i: Array<{
    p: string;
    q: number;
    u: number;
    t?: string;
  }>;
  u?: {
    i?: string;
    n?: string;
    e?: string;
    p?: string;
  };
  c?: string;
};

function toBase64Url(input: string): string {
  if (typeof window === 'undefined') return '';
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(input: string): string {
  if (typeof window === 'undefined') return '';
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

export function buildSharedCheckoutUrl(payload: SharedCartPayloadV1, origin: string): string {
  const compactPayload: SharedCartPayloadV2Compact = {
    v: 2,
    s: {
      i: payload.store.id,
      n: payload.store.name,
      g: payload.store.slug,
    },
    i: (payload.items || []).map((item) => ({
      p: item.product_id,
      q: Number(item.quantity) || 1,
      u: Number(item.unit_price) || 0,
      // Keep a short title hint but cap length for QR density
      t: item.title ? String(item.title).slice(0, 24) : undefined,
    })),
    c: payload.created_at,
  };

  const encoded = toBase64Url(JSON.stringify(compactPayload));
  return `${origin}/checkout?share=${encoded}`;
}

export function parseSharedCartFromQuery(search: string): SharedCartPayloadV1 | null {
  try {
    const params = new URLSearchParams(search);
    const share = params.get('share');
    if (!share) return null;
    const decoded = fromBase64Url(share);
    const parsed = JSON.parse(decoded) as SharedCartPayloadV1 | SharedCartPayloadV2Compact;

    if ((parsed as SharedCartPayloadV2Compact)?.v === 2) {
      const compact = parsed as SharedCartPayloadV2Compact;
      if (!compact?.s?.i || !Array.isArray(compact?.i)) return null;

      return {
        v: 1,
        created_at: compact.c || new Date().toISOString(),
        store: {
          id: compact.s.i,
          name: compact.s.n || 'Tienda',
          slug: compact.s.g,
        },
        items: compact.i.map((item) => ({
          product_id: item.p,
          quantity: Number(item.q) || 1,
          unit_price: Number(item.u) || 0,
          title: item.t || 'Producto',
        })),
      };
    }

    if ((parsed as SharedCartPayloadV1)?.v !== 1) return null;
    const legacy = parsed as SharedCartPayloadV1;
    if (!legacy?.store?.id || !Array.isArray(legacy?.items)) return null;
    return legacy;
  } catch {
    return null;
  }
}

export function buildTempStorageCartFromSharedPayload(payload: SharedCartPayloadV1) {
  return {
    store_id: payload.store.id,
    items: payload.items.map((item) => ({
      product_id: item.product_id,
      quantity: Number(item.quantity) || 1,
      unit_price: Number(item.unit_price) || 0,
      title: item.title || 'Producto',
    })),
    created_at: payload.created_at || new Date().toISOString(),
  };
}

export function buildCheckoutCartFromSharedPayload(payload: SharedCartPayloadV1, tempItems: any[]) {
  return {
    id: 'shared-cart',
    store_id: payload.store.id,
    user_id: 'shared',
    status: 'open',
    created_at: payload.created_at || new Date().toISOString(),
    stores: {
      id: payload.store.id,
      name: payload.store.name,
      slug: payload.store.slug,
    },
    cart_items: tempItems,
  };
}
